import React, { FC } from 'react';
import { Box, Button, IconButton, Paper, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { TitleWithValue } from 'components/title-with-value';
import { ListingEntry } from 'graphql/listings/queries';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { AmountField } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/Fields/AmountField';
import { PayeeField } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/Fields/PayeeField';
import { TaxField } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/Fields/TaxField';
import { ChargeDescriptionCard } from './ChargeDescriptionCard';
import { PartiesInvolvedSection } from './PartiesInvolvedSection';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  icon: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: '0.875rem',
    '&:hover': { color: theme.palette.common.black },
  },
  byOthersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  expandControlRow: {
    display: 'flex',
  },
  expandControl: {
    flex: 1,
    justifyContent: 'space-between',
    textTransform: 'none',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '5px',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.dark,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

interface Props {
  charge: ChargeFormEntry;
  index: number;
  onDelete: (rowIndex: number) => void;
  onEdit: (charge: ChargeFormEntry, rowIndex: number) => void;
  textPredectiveCharges: ListingEntry[];
  hideTax: boolean;
  currentLetter?: string;
  isOrderTypeNotHUD1: boolean;
  isPartiesInvolvedRowOpen: boolean;
  togglePartiesinvolvedRowOpen: () => void;
  onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
  onChargeAmountChange: (newAmount: number | null) => void;
  onChargePaidByAmountChange: () => void;
}

export const ChargeDocumentCard: FC<Props> = (props) => {
  const {
    charge,
    index,
    onDelete,
    onEdit,
    textPredectiveCharges,
    hideTax,
    currentLetter,
    isOrderTypeNotHUD1,
    isPartiesInvolvedRowOpen,
    togglePartiesinvolvedRowOpen,
    onEditSplitAmount,
    onChargeAmountChange,
    onChargePaidByAmountChange,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const isCharges1200 = currentLetter === '1200';

  return (
    <Paper className={classes.card}>
      <Box className={classes.headerRow}>
        <Tooltip title={t('orders:deleteDocumentTooltip')}>
          <IconButton className={classes.icon} onClick={() => onDelete(index)} size="large">
            <FontAwesomeIcon icon="trash-alt" size="sm" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('orders:editDocumentTooltip')}>
          <IconButton
            className={classes.icon}
            onClick={() => onEdit(charge, index)}
            size="large"
          >
            <FontAwesomeIcon icon="edit" />
          </IconButton>
        </Tooltip>
      </Box>

      <TitleWithValue title={t('charges:description')}>
        <ChargeDescriptionCard
          isDocument
          isReadOnly
          index={index}
          charge={charge}
          textPredectiveCharges={textPredectiveCharges}
        />
      </TitleWithValue>

      <TitleWithValue title={t('charges:payee')}>
        <PayeeField charge={charge} onOpenSplitModal={() => onEditSplitAmount(charge, 'Payee')} />
      </TitleWithValue>

      <TitleWithValue title={t('accounting:amount')}>
        <AmountField {...props} name="amount" onAmountChange={onChargeAmountChange} />
      </TitleWithValue>

      {!hideTax && (
        <TitleWithValue title="Tax">
          <TaxField charge={charge} onOpenSplitModal={() => onEditSplitAmount(charge, 'Tax')} />
        </TitleWithValue>
      )}

      <TitleWithValue
        title={
          isOrderTypeNotHUD1
            ? `${t('charges:paid-by-borrower')} - ${t('charges:at-closing')}`
            : t('charges:paidByBorrower')
        }
      >
        <AmountField {...props} name="buyer_at" onAmountChange={onChargePaidByAmountChange} />
      </TitleWithValue>

      {isOrderTypeNotHUD1 && (
        <TitleWithValue title={`${t('charges:paid-by-borrower')} - ${t('charges:before-closing')}`}>
          <AmountField {...props} name="buyer_before" onAmountChange={onChargePaidByAmountChange} />
        </TitleWithValue>
      )}

      {isCharges1200 === false && (
        <>
          <TitleWithValue title={`${t('charges:paid-by-seller')} - ${t('charges:at-closing')}`}>
            <AmountField {...props} name="seller_at" onAmountChange={onChargePaidByAmountChange} />
          </TitleWithValue>

          <TitleWithValue title={`${t('charges:paid-by-seller')} - ${t('charges:before-closing')}`}>
            <AmountField
              {...props}
              name="seller_before"
              onAmountChange={onChargePaidByAmountChange}
            />
          </TitleWithValue>

          <TitleWithValue title={t('charges:by-others')}>
            <Box className={classes.byOthersRow}>
              <AmountField
                {...props}
                name="by_others"
                onAmountChange={onChargePaidByAmountChange}
              />
              <FontAwesomeIcon
                icon="user"
                style={{ cursor: 'pointer' }}
                onClick={() => onEditSplitAmount(charge, 'Payer')}
              />
            </Box>
          </TitleWithValue>
        </>
      )}

      <Box className={classes.expandControlRow}>
        <Button
          fullWidth
          aria-label="expand row"
          aria-expanded={isPartiesInvolvedRowOpen}
          onClick={togglePartiesinvolvedRowOpen}
          className={classes.expandControl}
          endIcon={
            <FontAwesomeIcon
              icon={isPartiesInvolvedRowOpen ? 'chevron-up' : 'chevron-down'}
              size="sm"
            />
          }
        >
          {t('orders:parties-involved')}
        </Button>
      </Box>

      <PartiesInvolvedSection
        open={isPartiesInvolvedRowOpen}
        charge={charge}
        onEditParties={onEditSplitAmount}
      />
    </Paper>
  );
};
